import React, { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { useSearchParams } from 'next/navigation';

import appConfig from '@nerdwallet/app-config';

import type {
  PlaidLinkOnEvent,
  PlaidLinkOnExit,
  PlaidLinkOptions,
  PlaidLinkOptionsWithLinkToken,
} from 'react-plaid-link';

import { usePlaidLink } from 'react-plaid-link';

import { GET_IDENTITY_QUERY } from '@app/(compass)/home/dashboard/settings/notifications/queries';
import { NewPlaidLinkOnSuccess } from '@components/financial-institutions/plaid-sandbox/plaid-link';

import { getCookie, setCookie } from './cookies';
import {
  OAUTH_QUERY_PARAM,
  PLAID_OAUTH_REDIRECT,
} from '@components/financial-institutions/constants';
import { usePlaidModalVisibility } from '@components/financial-institutions/plaid-sandbox/plaid-modal-visibility-context';

export const initializeLinkTokenQuery = gql`
  query GetLinkTokenForLinking($redirectUri: String) {
    initializeLinkToken(redirectUri: $redirectUri) {
      token
    }
  }
`;

const PlaidLink = ({
  plaidLinkOnSuccess,
  plaidLinkOnEvent,
  plaidLinkOnExit,
  relinkPublicToken,
}: {
  plaidLinkOnSuccess: NewPlaidLinkOnSuccess;
  plaidLinkOnEvent: PlaidLinkOnEvent;
  plaidLinkOnExit: PlaidLinkOnExit;
  relinkPublicToken?: string;
}) => {
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const { isOAuth } = usePlaidModalVisibility();
  const numSecondsInDay = 60 * 60 * 24;

  const { data: identityData } = useQuery(GET_IDENTITY_QUERY);

  const { data: initializeLinkTokenData } = useQuery(initializeLinkTokenQuery, {
    fetchPolicy: 'no-cache',
    variables: {
      redirectUri: `${process.env.NEXT_PUBLIC_INTERNAL_BASE_URL}/home/account-link/oauth-redirect`,
    },
  });

  const { linkToken: oauthLinkToken } = getCookie(PLAID_OAUTH_REDIRECT) ?? {};

  // The user completed authenticating via OAuth and is resuming a Plaid Link instance if these fields are available:
  // - `oauthLinkToken` - Token used in the initial Plaid Link session
  const isOAuthLinking = oauthLinkToken && isOAuth;
  const newLinkToken = initializeLinkTokenData?.initializeLinkToken?.token;

  let linkToken;
  if (isOAuthLinking) {
    linkToken = oauthLinkToken;
  } else if (relinkPublicToken) {
    linkToken = relinkPublicToken;
  } else {
    linkToken = newLinkToken;
  }

  const getRedirectPath = () => {
    return `${location.pathname}${search ? `?${search}&` : '?'}${OAUTH_QUERY_PARAM}=`;
  };

  if (!isOAuthLinking) {
    setCookie(
      PLAID_OAUTH_REDIRECT,
      {
        identityId: identityData?.identity?.identityId,
        linkToken: linkToken,
        // redirectPath is used in my-nerdwallet to return the user back to where they started
        redirectPath: getRedirectPath(),
      },
      {
        // maxAge is how long the cookie should be valid for and was chosen arbitrarily
        maxAge: numSecondsInDay,
      },
    );
  }

  const config: PlaidLinkOptions = {
    // token must be the same token used for the first initialization of Link
    token: linkToken,
    onSuccess: (publicToken, metadata) =>
      plaidLinkOnSuccess(publicToken, metadata, linkToken),
    onEvent: plaidLinkOnEvent,
    onExit: plaidLinkOnExit,
    clientName: 'NerdWallet',
    env: appConfig.PLAID.ENVIRONMENT_NAME,
    product: ['transactions'],
    webhook: appConfig.PLAID.WEBHOOK_URL,
  };

  if (isOAuthLinking) {
    // receivedRedirectUri must include the query params
    (config as PlaidLinkOptionsWithLinkToken).receivedRedirectUri =
      window.location.href;
  }
  const { open, ready } = usePlaidLink(config);

  useEffect(() => {
    if (ready) {
      // reset overflow before opening Plaid modal
      // this allows PlaidLink to restore this style on close
      // so users retain the ability to scroll after close
      document.body.style.overflow = '';
      open();
    }
  }, [ready, open, isOAuthLinking]);

  return <></>;
};

export default PlaidLink;
